import React, { createContext, useContext, useEffect, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { useParams } from 'react-router-dom'
import { launchDicomViewerForReader } from '@common/utils/ohifUtils'
import { toast } from 'sonner'
import Toast from '@common/components/Toast'

type SigningModalState = {
  taskId: string
  isOpen: boolean
  isRawDataEnabled?: boolean
}

type SigningContextType = {
  modalState: SigningModalState | null
  openSigningModal: (taskId: string, isRawDataEnabled?: boolean) => void
  closeSigningModal: () => void
  isRestoringSession: boolean
}

const SigningContext = createContext<SigningContextType>({
  modalState: null,
  openSigningModal: () => {},
  closeSigningModal: () => {},
  isRestoringSession: false,
})

export const useSigningContext = () => useContext(SigningContext)

export const SigningContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [modalState, setModalState] = useState<SigningModalState | null>(null)
  const [isRestoringSession, setIsRestoringSession] = useState<boolean>(false)
  const { isAuthenticated, user } = useAuth()
  const { studyId } = useParams<{ studyId: string }>()

  const openSigningModal = (taskId: string, isRawDataEnabled?: boolean) => {
    setModalState({
      taskId,
      isOpen: true,
      isRawDataEnabled,
    })
  }

  const closeSigningModal = () => {
    setModalState(null)
  }

  useEffect(() => {
    const restoreModalState = async () => {
      const modalStateJSON = sessionStorage.getItem('reader_modal_state')

      if (modalStateJSON && isAuthenticated && user?.access_token) {
        try {
          setIsRestoringSession(true)
          const parsedState = JSON.parse(modalStateJSON)

          if (parsedState.taskId) {
            setModalState({
              taskId: parsedState.taskId,
              isOpen: true,
              isRawDataEnabled: parsedState.isRawDataEnabled,
            })

            if (studyId && user.access_token) {
              try {
                launchDicomViewerForReader(parsedState.taskId, studyId, parsedState.isRawDataEnabled ? 2 : 0)
              } catch (error) {
                console.error('Failed to launch DICOM viewer:', error)
                toast(<Toast message="Failed to launch viewer" variant="error" />)
              }
            }
          }

          sessionStorage.removeItem('reader_modal_state')
        } catch (error) {
          console.error('Failed to restore modal state:', error)
          toast(<Toast message="Failed to restore session" variant="error" />)
        } finally {
          setIsRestoringSession(false)
        }
      }
    }

    if (isAuthenticated && user?.access_token) {
      restoreModalState()
    }
  }, [isAuthenticated, user, studyId])

  return (
    <SigningContext.Provider
      value={{
        modalState,
        openSigningModal,
        closeSigningModal,
        isRestoringSession,
      }}
    >
      {children}
    </SigningContext.Provider>
  )
}
